import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { useHotkeys } from 'react-hotkeys-hook';
import { Tooltip } from '@material-ui/core';
import cx from 'classnames';

import { updateTriggerFolderFetch } from '../../../store/folders/foldersSlice';
import { GROUP, PATHS, RolesLowerCase, USER, USER_NEW } from '../../../enums';
import { ReactComponent as UserIcon } from '../../../assets/icons/user.svg';
import { patchUpdateMyCollaborativeFolder } from '../../../api/foldersApi';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { ReactComponent as SubgroupIcon } from '../../../assets/icons/folder-open.svg';
import useOnClickOutside from '../../../helpers/useOnClickOutside';
import { getGroups, getUsersByRolesArray } from '../../../api';
import styles from './EditMyCollaboratorModal.module.scss';
import useDebounce from '../../../helpers/useDebounce';
import { useAppDispatch } from '../../../store/hooks';
import { NOTIFICATION } from '../../../enums/ENUMS';
import RolePlate from '../../modules/RolePlate';
import Search from '../../modules/Search';
import Avatar from '../../modules/Avatar';
import Modal from '../../modules/Modal';
import { Portal } from '../../../helpers/bodyPortal';

type TEditMyCollaboratorModal = {
  open: boolean;
  setOpen: (bool: boolean) => void;
  currentUserId: string;
  alreadyCollaborators?: USER[];
  group_collaborators: GROUP[];
  folderId: string;
  ownerEmail: string;
  folder_name: string;
};

const EditMyCollaboratorModal: React.FC<TEditMyCollaboratorModal> = ({
  open,
  setOpen,
  currentUserId,
  alreadyCollaborators,
  group_collaborators,
  folderId,
  ownerEmail,
  folder_name,
}) => {
  const canViewEmails = useCanViewEmails();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const notification = useSnackbar();
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const [collaboratorsToRemove, setCollaboratorsToRemove] = useState<USER[]>([]);

  const [groups, setGroups] = useState<GROUP[]>(group_collaborators);
  useEffect(() => {
    if (open) {
      console.log('12123123123');
      setGroups(group_collaborators);
    }
  }, [open, group_collaborators]);

  const onRemoveUserClick = (user: USER) => {
    const arr = [...collaboratorsToRemove, user];
    setCollaboratorsToRemove(arr);
  };

  const [search, setSearch] = useState('');
  const [ignoreUsers, setIgnoreUsers] = useState<string[]>([currentUserId]);

  const [searchGroups, setSearchGroups] = useState('');

  useEffect(() => {
    if (alreadyCollaborators && alreadyCollaborators?.length) {
      setIgnoreUsers([...ignoreUsers, ...alreadyCollaborators.map((u) => u.id)]);
    }
  }, [alreadyCollaborators]);

  const [newCollaborators, setNewCollaborators] = useState<USER_NEW[]>([]);
  const onClose = () => {
    setOpen(false);
    setNewCollaborators([]);
    setSearch('');
    setSearchedUsers([]);
    setIgnoreUsers([currentUserId]);
    setCollaboratorsToRemove([]);
    setSearchGroups('');
    setSearchedGroups([]);
    setGroups(group_collaborators);
  };

  const createUrl = (idOfFolder?: string) => {
    let result = `${PATHS.VIDEOFEEDS_LIST.path}${idOfFolder ? `?folderId=${idOfFolder}` : ''}`;
    return result;
  };

  const submitNewFolder = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    setLoading(true);
    try {
      let urlPath = createUrl(folderId);

      await patchUpdateMyCollaborativeFolder(
        {
          collaboratorEmails: newCollaborators.map((u) => u.user.email),
          collaboratorsToRemove: collaboratorsToRemove.map((u) => u.email),
          groupIds: groups.map((g) => g.id),
          urlPath: encodeURI(urlPath),
        },
        folderId,
      );
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage(NOTIFICATION.SOMETHING_WENT_WRONG);
      }
    } finally {
      setTimeout(() => {
        dispatch(updateTriggerFolderFetch());
        onClose();
        setLoading(false);
      }, 500);
    }
  };

  const [searchedUsers, setSearchedUsers] = useState<USER_NEW[]>([]);
  const onCollaboratorsSearch = async () => {
    try {
      setLoading(true);
      const res = await getUsersByRolesArray([RolesLowerCase.ANALYST, RolesLowerCase.ORGANIZATION_ADMIN], search, ignoreUsers);
      setSearchedUsers(res.documents);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const [searchedGroups, setSearchedGroups] = useState<GROUP[]>([]);
  const onCollaboratorsGroupSearch = async () => {
    try {
      setLoading(true);
      const res = await getGroups({ search: searchGroups, filter_out_viewers: true, ignoreGroupIds: groups.map((g) => g.id) });
      setSearchedGroups(res.documents);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const debouncedValue = useDebounce(search, 1000);
  useEffect(() => {
    if (search) {
      onCollaboratorsSearch();
    }
  }, [debouncedValue]);

  const debouncedGroupSearch = useDebounce(searchGroups, 1000);
  useEffect(() => {
    if (debouncedGroupSearch) {
      onCollaboratorsGroupSearch();
    }
  }, [debouncedGroupSearch]);

  const onUserSelect = (selectedUser: USER_NEW) => {
    setNewCollaborators([...newCollaborators, selectedUser]);
    setSearch('');
    setSearchedUsers([]);
    setIgnoreUsers([...ignoreUsers, selectedUser.user.id]);
  };

  const onGroupSelect = (selectedGroup: GROUP) => {
    setGroups([...groups, selectedGroup]);
    setSearchedGroups([]);
  };

  const inputRef = useRef<any>(null);
  const groupInputRef = useRef<any>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [gPos, setGPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (inputRef?.current && open && groupInputRef?.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const gRect = groupInputRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom,
        left: rect.left,
      });
      setGPos({
        top: gRect.bottom,
        left: gRect.left,
      });
    }
  }, [open, inputRef, searchedUsers, groupInputRef, searchedGroups]);

  useHotkeys('Enter', () => {
    if (open && !loading) submitButtonRef?.current?.click();
  });

  const listRef = useRef<any>(null);
  const listGrouRef = useRef<any>(null);
  useOnClickOutside(listRef, (e) => {
    e.stopPropagation();
    setSearchedUsers([]);
  });

  useOnClickOutside(listGrouRef, (e) => {
    e.stopPropagation();
    setSearchedGroups([]);
  });

  const onGroupRemove = (id: string) => {
    const arr = groups.filter((g) => +g.id !== +id);
    console.log(arr, id);
    setGroups(arr);
  };

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <div className={styles.modalContainer}>
        <div className={styles.modalTitle}>Collaborators</div>
        <div className={styles.modalFolder}>{folder_name}</div>
        <form onSubmit={submitNewFolder} className={styles.form}>
          <div className={styles.modalTitle}>Add collaborators:</div>
          <Search
            placeHolder="Search users"
            onChange={(e) => setSearch(e.target.value)}
            className={cx(styles.searchField, !search ? styles.transparentSearch : '')}
          />
          <div className={styles.inputRef} ref={inputRef} />
          {search.length > 0 && searchedUsers.length > 0 && (
            <Portal>
              <div className={styles.form}>
                <div ref={listRef} className={styles.collaboratorsSearchList} style={{ top: pos.top, left: pos.left }}>
                  {searchedUsers.map((u) => (
                    <div className={styles.searchElement} key={u.user.email} onClick={() => onUserSelect(u)}>
                      <div className={styles.username}>{u?.user.username}</div>
                      {canViewEmails && (<div className={styles.email}>{u.user.email}</div>)}
                    </div>
                  ))}
                </div>
              </div>
            </Portal>
          )}

          {newCollaborators.length > 0 && (
            <div className={styles.collaboratorsList}>
              {newCollaborators.map((u) => (
                <div className={styles.user} key={u.user.email}>
                  <Avatar avatar={u.user.avatar_url} name={u.user.email} />{' '}
                  <div className={styles.info}>
                    <div className={styles.username}>{u?.user.username}</div>
                    {canViewEmails && (<div className={styles.email}>{u.user.email} </div>)}
                  </div>
                  <RolePlate className={styles.role} userRole={u?.role?.title} />
                </div>
              ))}
            </div>
          )}

          {alreadyCollaborators && alreadyCollaborators?.length > 0 && (
            <>
              <div className={styles.modalTitle}>Current collaborators:</div>
              <div className={styles.collaboratorsList}>
                {alreadyCollaborators
                  .filter((u) => !collaboratorsToRemove.find((ur) => ur.email === u.email))
                  .map((u) => (
                    <div className={styles.user} key={u.email}>
                      <Avatar avatar={u.avatar_url} name={u.email} />{' '}
                      <div className={styles.info}>
                        <div className={styles.username}>{u?.username}</div>
                        {canViewEmails && (<div className={styles.email}>{u.email}</div>)}
                      </div>
                      {u.email !== ownerEmail ? (
                        <ContainedButton onClick={() => onRemoveUserClick(u)} kind={'red'}>
                          {' '}
                          Remove{' '}
                        </ContainedButton>
                      ) : (
                        <ContainedButton>Creator</ContainedButton>
                      )}
                    </div>
                  ))}
              </div>
            </>
          )}

          <div className={styles.modalTitle}>Add groups:</div>
          <Search
            placeHolder="Search groups"
            onChange={(e) => setSearchGroups(e.target.value)}
            className={cx(styles.searchField, !searchGroups ? styles.transparentSearch : '')}
          />

          <div className={styles.inputRef} ref={groupInputRef} />
          {searchedGroups.length > 0 && (
            <Portal>
              <div className={styles.form}>
                <div ref={listGrouRef} className={styles.collaboratorsSearchList} style={{ top: gPos.top, left: gPos.left }}>
                  {searchedGroups.map((g) => (
                    <div className={styles.searcGroupElement} key={g.id} onClick={() => onGroupSelect(g)}>
                      <div className={styles.groupName}>{g.title}</div>
                      <div className={styles.email}>{g.analysts.length} people</div>
                    </div>
                  ))}
                </div>
              </div>
            </Portal>
          )}

          {groups.length > 0 && (
            <div className={styles.collaboratorsList}>
              {groups.map((g) => (
                <div className={styles.group} key={g.id}>
                  <div className={styles.name}>{g.title}</div>
                  <div className={styles.info}>
                    {(g.subgroups?.length || 0) > 0 && (
                      <Tooltip
                        arrow
                        interactive
                        title={
                          <div className={styles.tooltip}>
                            Subgroups:
                            {g.subgroups?.map((g) => (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {' '}
                                <SubgroupIcon /> {g.title}
                              </div>
                            ))}
                          </div>
                        }
                      >
                        <div className={styles.label}>
                          <SubgroupIcon /> {g.subgroups?.length}{' '}
                        </div>
                      </Tooltip>
                    )}

                    <Tooltip
                      arrow
                      interactive
                      title={
                        <div className={styles.tooltip}>
                          {g.analysts.map((u) => (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {' '}
                              <UserIcon /> {u?.username || (canViewEmails ? u?.email : 'User')}
                            </div>
                          ))}
                        </div>
                      }
                    >
                      <div>
                        <UserIcon /> {g.analysts.length}{' '}
                      </div>
                    </Tooltip>

                    <ContainedButton onClick={() => onGroupRemove(g.id)} className={styles.removeButton} kind={'red'}>
                      Remove
                    </ContainedButton>
                  </div>
                </div>
              ))}
            </div>
          )}

          <ContainedButton disabled={loading} className={styles.customBtn} type="submit">
            <div ref={submitButtonRef}>Save changes</div>
          </ContainedButton>
          <OutlinedButton kind="default" disabled={loading} onClick={onClose} type="button">
            Cancel
          </OutlinedButton>
        </form>
      </div>
    </Modal>
  );
};

export default EditMyCollaboratorModal;
